// src/store/index.ts
import { combineReducers, configureStore } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import { authTokensReducer } from '@/features/auth/authTokensSlice'
import { aiJobsReducer } from '@/features/ai/jobsSlice'
import { aiChatReducer } from '@/features/ai/chatSlice'
import { devReducer } from '@/features/dev/devSlice'
import { editorReducer } from '@/features/editor/editorSlice'
import { rateLimitReducer } from '@/features/rate-limit/rateLimitSlice'
import { playerProgressApi } from '@/features/player/playerProgressApi'
import { api } from './api'
import { idApi } from './idApi'
import { errorToastMiddleware } from './errorToastMiddleware'
import { draftProbeMiddleware } from '@/features/moderation/draftProbeMiddleware'
import { unsubmittedChangesMiddleware } from '@/features/moderation/unsubmittedChangesMiddleware'

const rootReducer = combineReducers({
  authTokens: authTokensReducer,
  aiJobs: aiJobsReducer,
  aiChat: aiChatReducer,
  dev: devReducer,
  editor: editorReducer,
  // Не в persist whitelist намеренно: перезагрузка страницы = новая попытка.
  rateLimit: rateLimitReducer,
  [api.reducerPath]: api.reducer,
  [idApi.reducerPath]: idApi.reducer,
  [playerProgressApi.reducerPath]: playerProgressApi.reducer,
})

const persistConfig = {
  key: 'root',
  storage,
  // aiJobs в whitelist: ручки «мои активные джобы» в api нет, подцепиться к
  // прогону после перезагрузки можно только по сохранённому job_id.
  // aiChat в whitelist по той же причине, что aiJobs: прогон переживает
  // перезагрузку, и чат, исчезающий при живом прогоне, читается как потеря.
  whitelist: ['authTokens', 'aiJobs', 'aiChat'],
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefault) =>
    getDefault({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(
      api.middleware,
      idApi.middleware,
      playerProgressApi.middleware,
      errorToastMiddleware,
      // Зонд — ПОСЛЕ `api.middleware`: его GET `/moderation` уходит раньше, чем RTK
      // перечитывает инвалидированный курс, и при `invalidationBehavior: 'delayed'`
      // (дефолт RTK) курс ждёт этот ответ и читает уже черновик. Переставить — вернуть
      // мигание живой версии после первой записи.
      draftProbeMiddleware,
      unsubmittedChangesMiddleware,
    ),
})

export const persistor = persistStore(store)

setupListeners(store.dispatch)

export type RootState = ReturnType<typeof rootReducer>
export type AppDispatch = typeof store.dispatch
